import {DependencyInjectId} from "@@/package/const/DependencyInjectId";
import {type ApplicationContextInterface} from "@@/package/application/applicationContext";
import type ProfileApi from "~~/package/apis/Profile/ProfileApi";
import {ProfileModel} from "~~/package/mapper/profile/model/ProfileModel";
import {ProfileUserInformationModel} from "~~/package/mapper/profile/model/ProfileUserInformationModel";
import {
    ProfileUserInformationWorkExperienceModel,
    ProfileWorkExperienceCreateModel
} from "~~/package/mapper/profile/model/ProfileUserInformationWorkExperienceModel";
import {
    ProfileEducationCreateModel,
    ProfileUserInformationEducationModel
} from "~~/package/mapper/profile/model/ProfileUserInformationEducationModel";
import type CustomResponse from "~~/package/types/ResponseType";


export default class ProfileMapper {
    protected profileApi: ProfileApi;

    constructor(context: ApplicationContextInterface) {
        this.profileApi = context.getService<ProfileApi>(DependencyInjectId.ProfileApi);
    }

    public async getUserMyProfile(lang: string): Promise<ProfileModel> {
        const profileResponseData: CustomResponse<ProfileModel> = (await this.profileApi.getUserMyProfile(lang)).data;
        return ProfileModel.create(profileResponseData.data);
    }

    public async getUserShareProfile(uuid: string, lang: string): Promise<ProfileModel> {
        const profileResponseData: CustomResponse<ProfileModel> = (await this.profileApi.getUserShareProfile(uuid, lang)).data;
        return profileResponseData.data;
    }

    public async getUserWorkExperienceList(isShare: boolean, lang: string, uuid: string): Promise<ProfileUserInformationWorkExperienceModel[]> {
        const profileResponseData: CustomResponse<ProfileUserInformationWorkExperienceModel[]> = (await this.profileApi.getUserWorkExperienceList(isShare, lang, uuid)).data;
        return profileResponseData.data.map((workExperience: ProfileUserInformationWorkExperienceModel) => ProfileUserInformationWorkExperienceModel.create(workExperience));
    }

    public async getUserEducationList(isShare: boolean, lang: string, uuid: string): Promise<ProfileUserInformationEducationModel[]> {
        const profileResponseData: CustomResponse<ProfileUserInformationEducationModel[]> = (await this.profileApi.getUserEducationList(isShare, lang, uuid)).data;
        return profileResponseData.data.map((education: ProfileUserInformationEducationModel) => ProfileUserInformationEducationModel.create(education));
    }

    public async getUserSkills(): Promise<string[]> {
        const profileResponseData: CustomResponse<string[]> = (await this.profileApi.getUserSkills()).data;
        return profileResponseData.data;
    }

    public async modifyUserProfile(profileData: ProfileUserInformationModel): Promise<ProfileUserInformationModel> {
        const profileResponseData: CustomResponse<ProfileUserInformationModel> = (await this.profileApi.modifyUserProfile(profileData)).data;
        return ProfileUserInformationModel.create(profileResponseData.data);
    }

    public async addAboutMe(title: string, content: string): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.addAboutMe(title, content)).data;
        return profileResponseData.data;
    }

    public async addWorkExperience(workExperience: ProfileWorkExperienceCreateModel): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.addWorkExperience(workExperience)).data;
        return profileResponseData.data;
    }

    public async addEducation(education: ProfileEducationCreateModel): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.addEducation(education)).data;
        return profileResponseData.data;
    }

    public async modifyAboutMe(id: number, title: string, content: string): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.modifyAboutMe(id, title, content)).data;
        return profileResponseData.data;
    }

    public async modifyWorkExperience(workExperience: ProfileWorkExperienceCreateModel): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.modifyWorkExperience(workExperience)).data;
        return profileResponseData.data;
    }

    public async modifyEducation(education: ProfileEducationCreateModel): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.modifyEducation(education)).data;
        return profileResponseData.data;
    }

    public async deleteAboutMe(id: number): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.deleteAboutMe(id)).data;
        return profileResponseData.data;
    }

    public async deleteWorkExperience(id: number): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.deleteWorkExperience(id)).data;
        return profileResponseData.data;
    }

    public async deleteEducation(id: number): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.deleteEducation(id)).data;
        return profileResponseData.data;
    }

    public async profileImageUpdate(formData: FormData): Promise<boolean> {
        const profileResponseData: CustomResponse<boolean> = (await this.profileApi.profileImageUpdate(formData)).data;
        return profileResponseData.data;
    }
}
